import { MAX_BATCH_BYTES, MAX_FILES } from './heif';

const RESERVED_FILENAME_CHARACTERS = new Set(['<', '>', ':', '"', '/', '\\', '|', '?', '*']);

export interface BatchChoice {
  accepted: File[];
  rejectedCount: number;
  reason?: string;
}

export function chooseFilesForBatch(
  existingCount: number,
  existingBytes: number,
  incoming: File[],
): BatchChoice {
  const accepted: File[] = [];
  let runningBytes = existingBytes;
  let rejectedCount = 0;
  let hitCountLimit = false;
  let hitSizeLimit = false;

  for (const file of incoming) {
    if (existingCount + accepted.length >= MAX_FILES) {
      rejectedCount += 1;
      hitCountLimit = true;
      continue;
    }

    if (runningBytes + file.size > MAX_BATCH_BYTES) {
      rejectedCount += 1;
      hitSizeLimit = true;
      continue;
    }

    accepted.push(file);
    runningBytes += file.size;
  }

  let reason: string | undefined;
  if (hitCountLimit && hitSizeLimit) {
    reason = 'Some photos were skipped to stay within 12 files and 100 MB.';
  } else if (hitCountLimit) {
    reason = 'Only 12 photos can be converted at once.';
  } else if (hitSizeLimit) {
    reason = 'Some photos were skipped to keep the batch under 100 MB.';
  }

  return { accepted, rejectedCount, reason };
}

export function outputName(inputName: string): string {
  const withoutExtension = inputName.replace(/\.(?:heic|heif)$/i, '');
  const clean = Array.from(withoutExtension, (character) => (
    character.charCodeAt(0) <= 31 || RESERVED_FILENAME_CHARACTERS.has(character)
      ? '_'
      : character
  )).join('').trim();
  return `${clean || 'photo'}.jpg`;
}

export function uniqueArchiveNames(names: string[]): string[] {
  const used = new Set<string>();
  return names.map((name) => {
    const key = name.toLocaleLowerCase();
    if (!used.has(key)) {
      used.add(key);
      return name;
    }

    const dot = name.lastIndexOf('.');
    const stem = dot > 0 ? name.slice(0, dot) : name;
    const extension = dot > 0 ? name.slice(dot) : '';
    let suffix = 2;
    let candidate = `${stem}-${suffix}${extension}`;
    while (used.has(candidate.toLocaleLowerCase())) {
      suffix += 1;
      candidate = `${stem}-${suffix}${extension}`;
    }
    used.add(candidate.toLocaleLowerCase());
    return candidate;
  });
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const megabytes = bytes / (1024 * 1024);
  if (megabytes >= 1) return `${megabytes.toFixed(megabytes >= 10 ? 0 : 1)} MB`;
  return `${Math.round(bytes / 1024)} KB`;
}
